import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { format } from "date-fns";
import { toast } from "sonner";
import { type DateString, DayStringFormat, type Mileage } from "../../types/presence";
import Modal from "../Modal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Typography from "../Typography";
import Divider from "../Divider";
import { useUserId } from "../../hooks/useUserId";
import { useVehiclePlates } from "../../hooks/data/use-settings-data";
import { PresenceTableName, useDynamoDeleteItem, useDynamoMutateItem } from "../../services/dynamo";

type Props = {
  date: Date;
  data?: Mileage;
  close: () => void;
};

type MileageFormData = {
  distance: number;
  destination: string;
  client: string;
  refound: number;
};

export default function MileageForm({ date, data, close }: Props) {
  const userId = useUserId();
  const { data: vehicles = [] } = useVehiclePlates(userId);
  const { mutateAsync: saveMileage } = useDynamoMutateItem<Mileage>(PresenceTableName, {});
  const { mutateAsync: deleteMileage } = useDynamoDeleteItem(PresenceTableName, {});
  const [isSaving, setIsSaving] = useState(false);

  const [selectedVehicleSk, setSelectedVehicleSk] = useState<string>(
    data?.vehicleSk ?? "",
  );

  // Once vehicles load, pick the pre-selected one (editing) or the first
  useEffect(() => {
    if (vehicles.length === 0 || selectedVehicleSk) return;
    setSelectedVehicleSk(data?.vehicleSk ?? vehicles[0].sk);
  }, [vehicles, selectedVehicleSk, data?.vehicleSk]);

  const selectedVehicle = vehicles.find((v) => v.sk === selectedVehicleSk);
  const euroPerKm = selectedVehicle?.euroPerKm ?? data?.euroPerKm ?? 0;

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<MileageFormData>({
    defaultValues: {
      distance: data?.distance ?? 0,
      destination: data?.destination ?? "",
      client: data?.client ?? "",
      refound: data?.refound ?? 0,
    },
  });

  const distance = watch("distance");

  useEffect(() => {
    const dist = typeof distance === "string" ? parseFloat(distance) : distance;
    if (!isNaN(dist) && !isNaN(euroPerKm)) {
      setValue("refound", parseFloat((dist * euroPerKm).toFixed(2)));
    }
  }, [distance, euroPerKm, setValue]);

  const onSubmit = async (formData: MileageFormData) => {
    if (!userId) return;
    setIsSaving(true);
    const toastId = toast.loading(data ? "Aggiornamento rimborso..." : "Salvataggio rimborso...");
    try {
      const skDate = format(date, DayStringFormat) as DateString;
      const sk: Mileage["sk"] = data?.sk ?? `mileage#${skDate}#${crypto.randomUUID()}`;
      await saveMileage({
        pk: userId,
        sk,
        date: skDate,
        type: "mileage",
        distance: Number(formData.distance),
        destination: formData.destination,
        client: formData.client.trim() || undefined,
        euroPerKm,
        refound: Number(formData.refound),
        vehicleSk: selectedVehicleSk || undefined,
      });
      toast.success(data ? "Rimborso aggiornato" : "Rimborso salvato", { id: toastId });
      close();
    } catch (err) {
      console.error(err);
      toast.error("Errore durante il salvataggio", { id: toastId });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!data) return;
    setIsSaving(true);
    const toastId = toast.loading("Eliminazione rimborso...");
    try {
      await deleteMileage({ key: { pk: data.pk, sk: data.sk } });
      toast.success("Rimborso eliminato", { id: toastId });
      close();
    } catch (err) {
      console.error(err);
      toast.error("Errore durante l'eliminazione", { id: toastId });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal open onClose={close} size="small">
      <div className="MileageForm">
        <div className="MileageForm__header">
          <Typography variant="h5" className="bold">
            {data ? "Modifica Rimborso Kilometrico" : "Aggiungi Rimborso Kilometrico"} per il{" "}
            {date.toLocaleDateString()}
          </Typography>
          <Typography variant="body2">
            Seleziona il veicolo e inserisci la distanza per calcolare il rimborso.
          </Typography>
          <Divider />
        </div>

        <form id="mileage-form" onSubmit={handleSubmit(onSubmit)}>
          <div className="MileageForm__body">
            <div className="MileageForm__field">
              <Typography variant="body2">Veicolo</Typography>
              <select
                className="MileageForm__select"
                value={selectedVehicleSk}
                onChange={(e) => setSelectedVehicleSk(e.target.value)}
              >
                {vehicles.map((v) => (
                  <option key={v.sk} value={v.sk}>
                    {v.brand} {v.model} — {v.plate}
                  </option>
                ))}
              </select>
            </div>

            <div className="MileageForm__rate">
              <Typography variant="body2">Tariffa applicata:</Typography>
              <Typography variant="body2" className="bold">
                {euroPerKm > 0 ? `${euroPerKm} €/km` : "—"}
              </Typography>
              {selectedVehicleSk && euroPerKm === 0 && (
                <span className="MileageForm__error">
                  Nessuna tariffa configurata per questo veicolo. Impostala nelle Impostazioni per poter salvare il rimborso.
                </span>
              )}
            </div>

            <div className="MileageForm__field">
              <Typography variant="body2">Distanza (km)</Typography>
              <Input
                autoFocus
                type="number"
                placeholder="Distanza (km)"
                min={0.1}
                step="any"
                {...register("distance", {
                  required: "Distanza richiesta",
                  valueAsNumber: true,
                  min: { value: 0.1, message: "Distanza deve essere maggiore di 0" },
                })}
              />
              {errors.distance && (
                <span className="MileageForm__error">{errors.distance.message}</span>
              )}
            </div>

            <div className="MileageForm__field">
              <Typography variant="body2">Destinazione</Typography>
              <Input
                type="text"
                placeholder="Destinazione"
                {...register("destination", { required: "Destinazione richiesta" })}
              />
              {errors.destination && (
                <span className="MileageForm__error">{errors.destination.message}</span>
              )}
            </div>

            <div className="MileageForm__field">
              <Typography variant="body2">Cliente</Typography>
              <Input
                type="text"
                placeholder="Cliente (opzionale)"
                {...register("client")}
              />
            </div>

            <div className="MileageForm__refound">
              <Typography variant="body2">Rimborso calcolato:</Typography>
              <Typography variant="body2" className="bold">
                {watch("refound").toFixed(2)} €
              </Typography>
            </div>
          </div>
        </form>

        <div className="MileageForm__buttons">
          {data && (
            <Button variant="ghost" type="button" onClick={handleDelete} disabled={isSaving}>
              Elimina
            </Button>
          )}
          <Button type="submit" form="mileage-form" disabled={isSaving || !selectedVehicleSk || euroPerKm === 0}>
            {data ? "Aggiorna" : "Salva"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

